import { useCallback, useEffect, useMemo, useState } from "react";

import { useBreakpoints } from "~/hooks/useBreakpoints";

import { Menu } from "../menu/Menu";
import { MenuItem } from "../menu/MenuItem";
import { Text } from "../text/Text";
import { Truncate } from "../truncate/Truncate";
import { SheetModal } from "../sheetModal/SheetModal";
import { Box } from "../box/Box";
import { Icon } from "../icon/Icon";
import { Divider } from "../divider/Divider";
import { ProgressRing } from "../progressRing/ProgressRing";
import { Seal } from "../seal/Seal";
import { DialogModal } from "../dialogModal/DialogModal";

import "./QuickNavigation.css";

import type { SyntheticEvent } from "react";

export enum QuickNavigationItemType {
  module,
  session,
  quiz,
  certificate,
  divider,
}

export interface QuickNavigationItem {
  artwork?: string;
  disabled?: boolean;
  id?: string;
  position?: number;
  postTitle?: string;
  preTitle?: string;
  progress?: Maybe<number>;
  title?: string;
  type: QuickNavigationItemType;
  url?: string;
  onClick?: (e: SyntheticEvent) => void;
}

export interface QuickNavigationProps {
  defaultOpen?: boolean;
  items?: QuickNavigationItem[];
  currentItemId?: string;
  preLabel?: string;
  label: string;
  onClose?: () => void;
  onOpen?: () => void;
}

export function QuickNavigation({
  defaultOpen = false,
  items,
  currentItemId,
  label,
  preLabel,
  onClose,
  onOpen,
}: QuickNavigationProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const breakpoints = useBreakpoints();

  const handleButtonClick = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const renderProgress = useCallback((value?: Maybe<number>) => {
    if (!value) return;
    if (value >= 1)
      return (
        <Icon
          icon="circle-check"
          variant="solid"
          color="var(--color-success)"
        />
      );
    return <ProgressRing value={value ?? 0} size="xs" />;
  }, []);

  const renderItem = useCallback(
    (item: QuickNavigationItem) => {
      switch (item.type) {
        case QuickNavigationItemType.module:
          return (
            <div className="QuickNavigationItem">
              <Box direction="column">
                <Text size="xs" weight="semibold" transform="uppercase">
                  {item.preTitle}
                </Text>
                <Text size="lg" weight="bold">
                  {item.title}
                </Text>
              </Box>

              <img
                className="QuickNavigationItem__artwork"
                src={item.artwork}
                alt={item.title ?? ""}
              />
            </div>
          );
        case QuickNavigationItemType.certificate:
          return (
            <div className="QuickNavigationItem">
              <Box direction="column">
                <Text size="xs" weight="semibold" transform="uppercase">
                  {item.preTitle}
                </Text>
                <Text size="lg" weight="bold">
                  {item.title}
                </Text>
              </Box>

              <div className="QuickNavigationItem__artwork">
                <Seal artwork={item.artwork} />
              </div>
            </div>
          );
        default:
          return (
            <div className="QuickNavigationItem">
              <Box direction="column" style={{ width: "100%" }}>
                <Text preset="pretitle">{item.preTitle}</Text>

                <Text weight="semibold">{item.title}</Text>

                <Box justify="space-between" align="center" direction="row">
                  <Text size="sm" color="gray">
                    {item.postTitle}
                  </Text>

                  {renderProgress(item.progress)}
                </Box>
              </Box>
            </div>
          );
      }
    },
    [renderProgress],
  );

  const renderItems = useCallback(() => {
    return items
      ?.reduce((acc, curr, index) => {
        if (
          [
            QuickNavigationItemType.module,
            QuickNavigationItemType.certificate,
          ].includes(curr.type) &&
          index > 0
        ) {
          acc.push({ type: QuickNavigationItemType.divider });
        }
        return acc.concat(curr);
      }, [] as QuickNavigationItem[])
      .map((item, index) =>
        item.type === QuickNavigationItemType.divider ? (
          <Divider key={index} />
        ) : (
          <MenuItem
            key={item.id}
            id={item.id}
            href={item.url}
            target="_self"
            disabled={item.disabled}
            onClick={
              item.onClick
                ? item.onClick
                : item.url
                  ? handleCloseModal
                  : undefined
            }
          >
            {renderItem(item)}
          </MenuItem>
        ),
      );
  }, [handleCloseModal, items, renderItem]);

  const renderedLabel = useMemo(() => {
    return (
      <>
        {preLabel && (
          <Text size="sm" lineHeight="tight" color="gray">
            {preLabel}
          </Text>
        )}
        <Text weight="semibold" lineHeight="tight">
          <Truncate lines={1}>{label}</Truncate>
        </Text>
      </>
    );
  }, [label, preLabel]);

  useEffect(() => {
    if (isOpen) {
      // if (currentItemId) {
      //   const currentItem = scrollContainerRef.current?.querySelector(
      //     `.MenuItem[data-id="${currentItemId}"]`,
      //   );
      //   currentItem?.scrollIntoView({
      //     block: "center",
      //   });
      // }
      onOpen?.();
    } else {
      onClose?.();
    }
  }, [currentItemId, isOpen, onClose, onOpen]);

  return (
    <div className="QuickNavigation">
      <button
        className="QuickNavigation__button"
        onClick={handleButtonClick}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        {renderedLabel}
      </button>

      {breakpoints.lg ? (
        <DialogModal
          isOpen={isOpen}
          size="sm"
          onRequestClose={handleCloseModal}
        >
          <DialogModal.Body>
            <Menu>{renderItems()}</Menu>
          </DialogModal.Body>
        </DialogModal>
      ) : (
        <SheetModal
          isOpen={isOpen}
          scrollable
          onRequestClose={handleCloseModal}
        >
          <SheetModal.Body>
            <Menu>{renderItems()}</Menu>
          </SheetModal.Body>
        </SheetModal>
      )}
    </div>
  );
}
